/**
 * Product Table - Hover Image Preview Functionality
 * Handles mouse and keyboard interactions for showing product image previews
 */

class ProductTable {
  constructor() {
    this.section = null;
    this.preview = null;
    this.currentRow = null;
    this.isMobile = window.innerWidth < 768;
    this.intersectionObserver = null;
    
    this.init();
  }

  init() {
    // Find the product table section
    this.section = document.querySelector('.product-table');
    if (!this.section) return;

    this.preview = this.section.querySelector('.product-table__preview');
    if (!this.preview) return;

    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.handleResize();
  }

  setupEventListeners() {
    if (!this.section) return;
    
    // Mouse events for desktop
    if (!this.isMobile) {
      this.section.addEventListener('mouseenter', this.handleMouseEnter.bind(this), { passive: true });
      this.section.addEventListener('mouseleave', this.handleMouseLeave.bind(this), { passive: true });
      this.section.addEventListener('mousemove', this.handleMouseMove.bind(this), { passive: true });
    }

    // Focus events for keyboard navigation
    this.section.addEventListener('focusin', this.handleFocusIn.bind(this));
    this.section.addEventListener('focusout', this.handleFocusOut.bind(this));

    // Window resize
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true });
  }

  setupIntersectionObserver() {
    if (!this.section) return;
    
    // Lazy load preview images when they come into view
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const row = entry.target;
          this.preloadImage(row);
        }
      });
    }, {
      rootMargin: '50px',
      threshold: 0.1
    });

    // Observe all product rows
    const rows = this.section.querySelectorAll('.product-table__row--product');
    rows.forEach(row => {
      if (row.hasAttribute('data-preview')) {
        this.intersectionObserver.observe(row);
      }
    });
  }

  preloadImage(row) {
    const preview = row.getAttribute('data-preview');
    const preview2x = row.getAttribute('data-preview-2x');
    
    if (preview && !row.dataset.imageLoaded) {
      const img = new Image();
      img.onload = () => {
        row.dataset.imageLoaded = 'true';
      };
      img.src = preview;
      
      if (preview2x) {
        const img2x = new Image();
        img2x.src = preview2x;
      }
    }
  }

  handleMouseEnter(event) {
    if (this.isMobile) return;
    
    const row = event.target.closest('.product-table__row--product');
    if (row && row.hasAttribute('data-preview')) {
      this.showPreview(row);
    }
  }

  handleMouseLeave(event) {
    if (this.isMobile) return;
    
    // Check if we're still within the section
    if (!this.section.contains(event.relatedTarget)) {
      this.hidePreview();
    }
  }

  handleMouseMove(event) {
    if (this.isMobile) return;
    
    const row = event.target.closest('.product-table__row--product');
    if (row && row.hasAttribute('data-preview') && row !== this.currentRow) {
      this.showPreview(row);
    }
    
    // Update preview position to follow mouse smoothly
    if (this.currentRow && this.preview) {
      this.positionPreview(event.clientX, event.clientY);
    }
  }

  handleFocusIn(event) {
    const row = event.target.closest('.product-table__row--product');
    if (row && row.hasAttribute('data-preview')) {
      this.showPreview(row);
    }
  }

  handleFocusOut(event) {
    // Check if focus is moving to another element within the section
    if (!this.section.contains(event.relatedTarget)) {
      this.hidePreview();
    }
  }

  showPreview(row) {
    if (!row || !row.hasAttribute('data-preview') || row === this.currentRow) return;

    this.currentRow = row;
    const preview = row.getAttribute('data-preview');
    const preview2x = row.getAttribute('data-preview-2x');
    
    if (!preview) return;

    // Update preview image
    const previewImg = this.preview.querySelector('.product-table__preview-image');
    if (previewImg) {
      previewImg.src = preview;
      if (preview2x) {
        previewImg.srcset = `${preview} 1x, ${preview2x} 2x`;
      }
      previewImg.alt = `Preview of ${row.querySelector('.product-table__product-title')?.textContent || 'product'}`;
    }

    // Show preview - positioning will be handled by mousemove
    this.preview.classList.add('is-visible');
  }

  hidePreview() {
    this.currentRow = null;
    this.preview.classList.remove('is-visible');
  }

  positionPreview(mouseX, mouseY) {
    if (!this.preview || this.isMobile) return;

    const sectionRect = this.section.getBoundingClientRect();
    const previewRect = this.preview.getBoundingClientRect();
    
    // Calculate position relative to the section
    let left = mouseX - sectionRect.left - (previewRect.width / 2);
    let top = mouseY - sectionRect.top - (previewRect.height / 2);
    
    // Ensure preview doesn't go outside the section bounds
    if (left < 0) {
      left = 0;
    } else if (left + previewRect.width > sectionRect.width) {
      left = sectionRect.width - previewRect.width;
    }
    
    if (top < 0) {
      top = 0;
    } else if (top + previewRect.height > sectionRect.height) {
      top = sectionRect.height - previewRect.height;
    }
    
    // Apply position using transform for smooth movement
    this.preview.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;
    
    // If switching between mobile and desktop, reset preview state
    if (wasMobile !== this.isMobile) {
      this.hidePreview();
      
      if (this.isMobile) {
        // Reset preview positioning for mobile
        this.preview.style.transform = '';
      }
    }
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductTable();
  });
} else {
  new ProductTable();
}

// Handle Shopify section updates
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('product-table')) {
    new ProductTable();
  }
});

// Handle Shopify section unload
document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.classList.contains('product-table')) {
    // Clean up any existing instances
    const instances = event.target._productTableInstance;
    if (instances) {
      instances.destroy();
    }
  }
});
